/**
 * Pure helpers for coupon soft-delete behavior (Order Settings Snapshot
 * Plan, Fix 2). Extracted as a separate module so they can be unit-tested
 * without mounting the full CampaignCoupons.vue or wrestling with the
 * Vuetify test harness.
 *
 * Default-true semantics: a coupon entry is considered active unless
 * `active === false`. Legacy entries (no `active` field at all) are
 * treated as active — soft-delete was added after some campaigns already
 * had coupon data persisted.
 */

/**
 * @param {object|null|undefined} coupon
 * @returns {boolean} true if the coupon is active (or has no `active` field)
 */
export function isCouponActive(coupon) {
  if (!coupon) return false;
  return coupon.active !== false;
}

/**
 * @param {object} coupon
 * @param {boolean} showDeleted whether the "Show deleted" toggle is on
 * @returns {boolean} whether this coupon row should be visible in the
 *   admin coupon list
 */
export function isCouponVisible(coupon, showDeleted) {
  return isCouponActive(coupon) || !!showDeleted;
}

/**
 * @param {Array<object>} coupons
 * @returns {boolean} true if any coupon in the list has been soft-deleted
 */
export function hasDeletedCoupons(coupons) {
  if (!Array.isArray(coupons)) return false;
  return coupons.some((c) => c && c.active === false);
}

/**
 * Soft-delete: returns a new coupon with `active: false`. If the coupon
 * has never been persisted (no `name`), returns null to signal that the
 * caller should hard-remove it (no historical-order risk for unsaved
 * entries).
 *
 * @param {object} coupon
 * @returns {object|null} updated coupon, or null if it should be hard-removed
 */
export function softDeleteCoupon(coupon) {
  if (!coupon) return null;
  if (!coupon.name) return null;
  return { ...coupon, active: false };
}

/**
 * Reactivate a soft-deleted coupon.
 *
 * @param {object} coupon
 * @returns {object} updated coupon with `active: true`
 */
export function reactivateCoupon(coupon) {
  if (!coupon) return coupon;
  return { ...coupon, active: true };
}

/**
 * Build a fresh coupon entry suitable for pushing into the coupon list
 * when the admin clicks "Add Coupon". New entries are explicitly active
 * so the data shape is consistent going forward.
 *
 * @returns {object} a new coupon object
 */
export function buildNewCoupon() {
  return { coupon_type: "Flat Rate", active: true };
}

/**
 * Unambiguous alphabet for generated coupon-code suffixes: excludes
 * characters that are easy to mis-key or mis-read (O/0, I/1/l).
 */
const UNAMBIGUOUS_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

/**
 * Generates a single random coupon-code suffix drawn from the
 * unambiguous alphabet. Uses Math.random -- app code (as opposed to
 * workflow/tooling scripts) is not subject to the project's
 * Math.random ban, and no cryptographic property is needed here (it's
 * a human-typed promo code, not a security token).
 *
 * @param {number} length
 * @returns {string}
 */
function randomSuffix(length) {
  let out = "";
  for (let i = 0; i < length; i++) {
    out += UNAMBIGUOUS_ALPHABET[
      Math.floor(Math.random() * UNAMBIGUOUS_ALPHABET.length)
    ];
  }
  return out;
}

/**
 * Builds a single "PREFIX-SUFFIX" style coupon code. If the prefix
 * already ends with a non-alphanumeric character, the separator is not
 * doubled (e.g. prefix "SUMMER-" + separator "-" => "SUMMER-A4K9", not
 * "SUMMER--A4K9").
 *
 * @param {string} prefix
 * @param {string} separator
 * @param {number} suffixLength
 * @returns {string}
 */
export function generateCouponCode(prefix, separator, suffixLength) {
  const trimmedPrefix = (prefix || "").trim();
  const endsWithNonAlphanumeric =
    trimmedPrefix.length > 0 && !/[A-Za-z0-9]$/.test(trimmedPrefix);
  const joiner = endsWithNonAlphanumeric ? "" : separator;
  return `${trimmedPrefix}${joiner}${randomSuffix(suffixLength)}`;
}

/**
 * Generates `count` unique coupon objects for the "Generate Coupons"
 * bulk-create modal. Auto-retries on collision (against both
 * existingCodes and codes already produced in this batch) until it has
 * `count` unique codes, or bails out with a clear error rather than
 * looping forever if the suffix space can't fit the requested count.
 *
 * Shape matches the hand-added coupon format exactly, including the
 * space in the "flat rate" key:
 *   { name, coupon_type, percentage | "flat rate", active: true }
 *
 * @param {object} opts
 * @param {string} opts.prefix required; all codes start with this
 * @param {number} opts.count number of unique coupons to generate
 * @param {"Percentage"|"Flat Rate"} opts.type
 * @param {number} opts.value the percentage or flat-rate amount
 * @param {string[]} [opts.existingCodes] codes that must not collide
 * @param {string} [opts.separator] defaults to "-"
 * @param {number} [opts.suffixLength] defaults to a length that
 *   comfortably fits `count` (4-6 chars)
 * @param {number} [opts.maxAttempts] safety cap on generation attempts
 * @returns {Array<object>} `count` unique coupon objects
 */
export function generateCoupons({
  prefix,
  count,
  type,
  value,
  existingCodes = [],
  separator = "-",
  suffixLength,
  maxAttempts,
}) {
  const resolvedSuffixLength =
    suffixLength || (count > 200 ? 6 : count > 50 ? 5 : 4);
  const resolvedMaxAttempts = maxAttempts || Math.max(count * 50, 1000);

  const existing = new Set(existingCodes || []);
  const generated = [];
  const generatedNames = new Set();
  let attempts = 0;

  while (generated.length < count) {
    if (attempts >= resolvedMaxAttempts) {
      throw new Error(
        `Could not generate ${count} unique coupon codes with prefix ` +
          `"${prefix}" (suffix length ${resolvedSuffixLength}) after ` +
          `${attempts} attempts. Try a shorter prefix requirement, a ` +
          `smaller count, or a longer suffix.`
      );
    }
    attempts++;

    const code = generateCouponCode(prefix, separator, resolvedSuffixLength);
    if (existing.has(code) || generatedNames.has(code)) {
      continue;
    }

    generatedNames.add(code);
    const coupon = {
      name: code,
      coupon_type: type,
      active: true,
    };
    if (type === "Flat Rate") {
      coupon["flat rate"] = value;
    } else {
      coupon["percentage"] = value;
    }
    generated.push(coupon);
  }

  return generated;
}

/**
 * Formats a coupon's amount + type into the display string used by the
 * read-only coupon list row, e.g. "10% off order total" or
 * "$1.50 off order total".
 *
 * @param {object} coupon
 * @returns {string} display text, or "" if the amount is missing
 */
export function formatCouponAmount(coupon) {
  if (!coupon) return "";
  if (coupon.coupon_type === "Percentage") {
    const value = coupon.percentage;
    if (value === undefined || value === null || value === "") return "";
    return `${value}% off order total`;
  }
  // Flat Rate (or any non-Percentage type defaults to the flat-rate key)
  const value = coupon["flat rate"];
  if (value === undefined || value === null || value === "") return "";
  return `$${value} off order total`;
}

/**
 * Returns the raw numeric amount for a coupon: the percentage for a
 * Percentage coupon, or the "flat rate" value otherwise. Used by the CSV
 * export, where a bare number is friendlier for spreadsheets than the
 * formatted "$X off order total" string. Returns "" when the value is
 * missing so the CSV cell is simply empty.
 *
 * @param {object} coupon
 * @returns {number|string} the raw amount, or "" if missing
 */
export function couponRawAmount(coupon) {
  if (!coupon) return "";
  const value =
    coupon.coupon_type === "Percentage"
      ? coupon.percentage
      : coupon["flat rate"];
  if (value === undefined || value === null || value === "") return "";
  return value;
}

/**
 * RFC-4180 CSV field escaping: if the field contains a comma, double
 * quote, or newline, wrap it in double quotes and double any embedded
 * quotes. Otherwise return it unchanged.
 *
 * @param {*} value coerced to string first
 * @returns {string}
 */
function csvEscape(value) {
  let str = value === undefined || value === null ? "" : String(value);
  // CSV formula-injection guard (defense-in-depth): a field starting with
  // =, +, -, @ (or a leading tab/CR) is interpreted as a formula by Excel /
  // Google Sheets when the export is opened. Coupon codes are admin-entered,
  // so prefix a leading trigger char with a single quote to neutralize it.
  // Benign codes (alphanumeric-leading) are untouched.
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Builds the coupon-export CSV as a single string: a fixed
 * "Code,Type,Amount,Status" header followed by one row per coupon, in
 * order. Columns:
 *   - Code:   coupon.name
 *   - Type:   coupon.coupon_type ("Percentage" | "Flat Rate")
 *   - Amount: the raw numeric value (couponRawAmount)
 *   - Status: "Active" | "Deleted" (default-true: no `active` => Active)
 * All fields are RFC-4180 escaped (coupon codes may contain commas or
 * quotes). Extracted as a pure function so it can be unit-tested without
 * triggering a real browser download.
 *
 * @param {Array<object>} coupons
 * @returns {string} CSV text (header only for an empty/nullish list)
 */
export function buildCouponCsv(coupons) {
  const header = "Code,Type,Amount,Status";
  if (!Array.isArray(coupons) || coupons.length === 0) return header;
  const rows = coupons.map((c) => {
    const code = csvEscape(c && c.name);
    const type = csvEscape(c && c.coupon_type);
    const amount = csvEscape(couponRawAmount(c));
    const status = csvEscape(isCouponActive(c) ? "Active" : "Deleted");
    return `${code},${type},${amount},${status}`;
  });
  // LF line terminator. RFC-4180 specifies CRLF, but Excel and Google
  // Sheets both import LF-delimited CSV correctly; keeping LF avoids trailing
  // \r artifacts in downstream string handling. (Bar-raiser P2, deferred.)
  return [header, ...rows].join("\n");
}

/**
 * Storefront-side validator: find an applicable coupon entry by name.
 * Soft-deleted entries (`active === false`) must NOT match — a deleted
 * coupon should not be applicable to a new order. Default-true: legacy
 * entries with no `active` field still match.
 *
 * @param {Array<object>} coupons campaign.coupons
 * @param {string} code shopper-entered coupon code
 * @returns {object|undefined} the matching coupon, or undefined
 */
export function findApplicableCoupon(coupons, code) {
  if (!Array.isArray(coupons) || !code) return undefined;
  return coupons.find(
    (c) => c && c.name === code && c.active !== false
  );
}
